import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Phone, User, Building2, Package, ChevronRight, MapPin } from "lucide-react";
import { toast } from "sonner";
import { API, getSessionId } from "@/lib/api";
import { WIDGET } from "@/constants/testIds";
import { INDONESIAN_PROVINCES } from "@/constants/provinces";

const QUICK_REPLIES = [
  "Fiber Optic & FTTH",
  "OLT / ONU FASTLINK",
  "MATV & CATV",
  "Splicer JILONG",
  "Minta Penawaran",
];

const INITIAL_BOT_MESSAGE = {
  role: "assistant",
  content:
    "Halo! Saya Falcom Assistant dari **PT Mitra Kabel Indonesia**. Kami distributor perangkat FTTH, Fiber Optic, LAN, MATV/CATV, dan tools splicing dengan kantor di **Surabaya & Jakarta**.\n\nAda yang bisa saya bantu hari ini? 🙂",
};

function formatText(text) {
  // Simple markdown: **bold** and line breaks
  const parts = text.replace(/\[SHOW_LEAD_FORM\]/g, "").split(/(\*\*[^*]+\*\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-slate-900">
          {p.slice(2, -2)}
        </strong>
      );
    }
    return <span key={i}>{p}</span>;
  });
}

function LeadForm({ onSubmit, onSkip }) {
  const [form, setForm] = useState({ name: "", company: "", phone: "", province: "", city: "", needs: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.name.trim() ||
      !form.company.trim() ||
      !form.phone.trim() ||
      !form.province.trim() ||
      !form.city.trim() ||
      !form.needs.trim()
    ) {
      toast.error("Mohon lengkapi semua data");
      return;
    }
    setLoading(true);
    try {
      await onSubmit(form);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid={WIDGET.leadFormCard}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3 my-2"
    >
      <div className="text-sm font-semibold text-slate-900 font-display">
        Tinggalkan Data Anda
      </div>
      <div className="text-xs text-slate-500 -mt-2">
        Tim sales kami akan menghubungi dalam 1×24 jam.
      </div>
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="relative">
          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid={WIDGET.leadNameInput}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="Nama lengkap"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </div>
        <div className="relative">
          <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid={WIDGET.leadCompanyInput}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="Nama perusahaan"
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </div>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid={WIDGET.leadPhoneInput}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="No. WhatsApp / Telepon"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </div>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          <select
            data-testid={WIDGET.leadProvinceSelect}
            className={`w-full pl-9 pr-8 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent appearance-none ${
              form.province ? "text-slate-900" : "text-slate-400"
            }`}
            value={form.province}
            onChange={(e) => setForm({ ...form, province: e.target.value })}
          >
            <option value="" disabled>Pilih Provinsi</option>
            {INDONESIAN_PROVINCES.map((p) => (
              <option key={p} value={p} className="text-slate-900">{p}</option>
            ))}
          </select>
          <ChevronRight className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 rotate-90 pointer-events-none" />
        </div>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            data-testid={WIDGET.leadCityInput}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            placeholder="Kabupaten / Kota"
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
          />
        </div>
        <div className="relative">
          <Package className="absolute left-3 top-3 w-4 h-4 text-slate-400" />
          <textarea
            data-testid={WIDGET.leadNeedsInput}
            className="w-full pl-9 pr-3 py-2 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
            rows={2}
            placeholder="Kebutuhan produk / proyek"
            value={form.needs}
            onChange={(e) => setForm({ ...form, needs: e.target.value })}
          />
        </div>
        <div className="flex gap-2 pt-1">
          <button
            data-testid={WIDGET.leadSubmitBtn}
            type="submit"
            disabled={loading}
            className="flex-1 bg-[#DC2626] hover:bg-[#B91C1C] text-white text-sm font-medium py-2 px-3 rounded-lg transition-colors disabled:opacity-60"
          >
            {loading ? "Mengirim..." : "Kirim"}
          </button>
          <button
            data-testid={WIDGET.leadSkipBtn}
            type="button"
            onClick={onSkip}
            className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
          >
            Nanti saja
          </button>
        </div>
      </form>
    </motion.div>
  );
}

export default function ChatWidget({ config }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([INITIAL_BOT_MESSAGE]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [showLeadForm, setShowLeadForm] = useState(false);
  const [leadDone, setLeadDone] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());

  const whatsappLink = config?.whatsapp_link || "https://wa.me/628113195050";

  // If mounted inside an iframe (embed mode), notify the parent window
  // whenever the widget open/closed state changes so the host page can
  // resize the iframe (compact launcher vs expanded panel).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.parent === window) return; // not embedded
    try {
      window.parent.postMessage(
        { source: "falcom-widget", type: "state", open },
        "*"
      );
    } catch (_e) {
      // ignore cross-origin errors
    }
  }, [open]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (window.parent === window) return;
    try {
      window.parent.postMessage(
        { source: "falcom-widget", type: "ready", open: false },
        "*"
      );
    } catch (_e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, streaming, showLeadForm]);

  async function sendMessage(text) {
    if (!text.trim() || streaming) return;
    const userMsg = { role: "user", content: text };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setInput("");
    setStreaming(true);

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text }),
      });
      if (!res.ok || !res.body) throw new Error("Stream failed");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = {
                  role: "assistant",
                  content: copy[copy.length - 1].content + data.delta,
                };
                return copy;
              });
            }
            if (data.done) {
              if (data.show_lead_form && !leadDone) {
                setShowLeadForm(true);
              }
            }
          } catch (_e) {
            // ignore malformed SSE chunks
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Maaf, terjadi kendala koneksi. Silakan coba lagi.",
        };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  }

  async function submitLead(form) {
    try {
      const res = await fetch(`${API}/leads`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, session_id: sessionId.current }),
      });
      if (!res.ok) throw new Error(`Lead submission failed: HTTP ${res.status}`);
      setShowLeadForm(false);
      setLeadDone(true);
      setMessages((m) => [
        ...m,
        {
          role: "assistant",
          content:
            `Terima kasih, **${form.name}**! Data Anda sudah kami terima. Tim sales Falcom akan menghubungi Anda ke nomor **${form.phone}** dalam 1×24 jam.\n\nAda hal lain yang bisa saya bantu?`,
        },
      ]);
      toast.success("Data berhasil dikirim");
    } catch (e) {
      toast.error("Gagal mengirim data. Coba lagi.");
    }
  }

  return (
    <>
      {/* Launcher */}
      <motion.button
        data-testid={WIDGET.launcher}
        aria-label={open ? "Tutup chat" : "Buka chat"}
        aria-expanded={open}
        onClick={() => setOpen(!open)}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        className="fixed bottom-6 right-6 z-[9998] w-14 h-14 rounded-2xl bg-[#DC2626] hover:bg-[#B91C1C] text-white flex items-center justify-center shadow-[0_8px_24px_rgba(220,38,38,0.4)] transition-colors"
      >
        <AnimatePresence mode="wait">
          {open ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <X className="w-6 h-6" />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ rotate: 90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: -90, opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              <MessageCircle className="w-6 h-6" />
            </motion.div>
          )}
        </AnimatePresence>
        {!open && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-400 rounded-full ring-2 ring-white animate-pulse" />
        )}
      </motion.button>

      {/* Panel */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={WIDGET.panel}
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", stiffness: 260, damping: 20 }}
            className="fixed bottom-24 right-6 z-[9999] w-[calc(100vw-3rem)] sm:w-[380px] h-[600px] max-h-[85vh] bg-white rounded-2xl border border-slate-200 shadow-[0_8px_32px_rgba(0,0,0,0.12)] flex flex-col overflow-hidden"
          >
            {/* Header */}
            <div className="bg-[#DC2626] text-white px-4 py-3 flex items-center gap-3 flex-shrink-0">
              <div className="w-10 h-10 rounded-full bg-white/15 backdrop-blur flex items-center justify-center flex-shrink-0">
                <div className="w-3 h-3 rounded-full bg-emerald-400 absolute translate-x-3 translate-y-3 ring-2 ring-[#DC2626]" />
                <MessageCircle className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display font-bold text-[15px] leading-tight">
                  Falcom Assistant
                </div>
                <div className="text-[11px] text-white/80 leading-tight flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                  Online
                </div>
              </div>
              <a
                data-testid={WIDGET.whatsappBtn}
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-lg bg-[#25D366] hover:bg-[#1EBE5A] flex items-center justify-center transition-colors"
                aria-label="Chat via WhatsApp"
                title="Chat via WhatsApp"
              >
                <Phone className="w-4 h-4" />
              </a>
              <button
                data-testid={WIDGET.closeBtn}
                onClick={() => setOpen(false)}
                className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center transition-colors"
                aria-label="Tutup"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div
              ref={scrollRef}
              className="chat-scroll flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-white"
            >
              {messages.map((msg, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                >
                  <div
                    data-testid={msg.role === "user" ? WIDGET.messageUser : WIDGET.messageBot}
                    className={`max-w-[85%] px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                      msg.role === "user"
                        ? "bg-[#DC2626] text-white rounded-2xl rounded-tr-sm"
                        : "bg-slate-100 text-slate-900 rounded-2xl rounded-tl-sm"
                    }`}
                  >
                    {msg.content ? formatText(msg.content) : (
                      <div className="flex items-center py-1">
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                        <span className="typing-dot" />
                      </div>
                    )}
                  </div>
                </motion.div>
              ))}

              {showLeadForm && (
                <LeadForm onSubmit={submitLead} onSkip={() => setShowLeadForm(false)} />
              )}

              {/* Quick replies (only at start) */}
              {messages.length === 1 && !streaming && (
                <div className="pt-2">
                  <div className="text-[11px] uppercase tracking-wider text-slate-400 mb-2 font-medium">
                    Kategori Populer
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {QUICK_REPLIES.map((q) => (
                      <button
                        key={q}
                        data-testid={`${WIDGET.quickReply}-${q.replace(/\s+/g, "-").toLowerCase()}`}
                        onClick={() => sendMessage(q)}
                        className="rounded-full border border-red-200 bg-red-50 text-red-700 px-3 py-1.5 text-xs font-medium hover:bg-red-100 transition-colors whitespace-nowrap"
                      >
                        {q}
                        <ChevronRight className="w-3 h-3 inline ml-0.5 -mr-1" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Input */}
            <div className="border-t border-slate-200 px-3 py-3 flex-shrink-0 bg-white">
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMessage(input);
                }}
                className="flex gap-2 items-end"
              >
                <input
                  data-testid={WIDGET.input}
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder="Ketik pesan Anda..."
                  disabled={streaming}
                  className="flex-1 px-3 py-2.5 text-sm bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-60"
                />
                <button
                  data-testid={WIDGET.sendBtn}
                  type="submit"
                  disabled={!input.trim() || streaming}
                  className="w-10 h-10 rounded-xl bg-[#DC2626] hover:bg-[#B91C1C] text-white flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
                  aria-label="Kirim"
                >
                  <Send className="w-4 h-4" />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
